import TextWinds from '../../common/TextWinds';
import { TermType } from '../../common/Term';
import { TermAgreement } from '../../common/TermAgreement';
import FormRow from '../../common/input/FormRow';
import { Control, FieldErrors, useWatch } from 'react-hook-form';
import { PutStudentFirstLoginRequest } from '../../../api/utils/student/first-login/apis';
import React from 'react';
import ErrorInput from '../../common/ErrorInput';
import {
  birthdayRules,
  COUNTRY_CODE_HELP,
  COUNTRY_CODE_REQUIRED_MESSAGE,
  countryCodeRules,
  DEFAULT_COUNTRY_CODE,
  nameRules,
  phoneRules,
} from '../../../constants/validation';
import InfoIcon from '../../common/icon/InfoIcon';

interface GuardianInputProps {
  control: Control<PutStudentFirstLoginRequest>;
  errors: FieldErrors<PutStudentFirstLoginRequest>;
  setAgree: (value: boolean) => void;
}

/**
 * 학생 첫 로그인 > 보호자 정보 입력
 *  */
const GuardianInput = ({ control, errors, setAgree }: GuardianInputProps) => {
  /** 전화번호의 검사 기준과 표기가 국가 코드에 따라 달라지므로 입력값을 구독한다. */
  const countryCode = useWatch({
    control,
    name: 'guardianInfo.countryCode',
  });

  const agreement = [
    { termType: TermType.PRIVACY_POLICY, essential: true },
    { termType: TermType.PRIVACY_CONSENT, essential: true },
  ];

  return (
    <section className="mt-4">
      {/* 타이틀 */}
      <TextWinds type="title_h3">보호자 정보</TextWinds>

      {/* 안내 메시지 */}
      <div className="mt-4 flex w-full items-center justify-start gap-2">
        <InfoIcon className="size-6" />
        <div className="text-sm font-medium leading-snug text-blue-500">
          만 14세 미만의 어린이는 보호자의 동의가 필요합니다.
        </div>
      </div>

      {/* 보호자 정보 입력 폼 */}
      <div className="mt-4 flex flex-col gap-4 rounded-lg bg-gray0 p-4">
        <div>
          <TextWinds type={'content_body2'}>보호자 이름</TextWinds>
          <FormRow
            control={control}
            type="text"
            name="guardianInfo.name"
            placeholder="보호자 이름"
            rules={nameRules({ label: '보호자 이름', required: true })}
            error={!!errors.guardianInfo?.name}
          />
          <div className="min-h-2">
            {errors.guardianInfo?.name && (
              <ErrorInput text={errors.guardianInfo.name.message} />
            )}
          </div>
        </div>

        <div>
          <TextWinds type={'content_body2'}>보호자 생년월일</TextWinds>
          <FormRow
            control={control}
            type="birthday"
            name="guardianInfo.birthday"
            rules={birthdayRules({ label: '보호자 생년월일', required: true })}
            error={!!errors.guardianInfo?.birthday}
          />
          <div className="min-h-2">
            {errors.guardianInfo?.birthday && (
              <ErrorInput text={errors.guardianInfo.birthday.message} />
            )}
          </div>
        </div>

        <div>
          <TextWinds type={'content_body2'}>보호자 성별</TextWinds>
          <FormRow
            control={control}
            type="gender"
            name="guardianInfo.gender"
            placeholder="보호자 성별"
            rules={{
              required: '보호자 성별을 선택해주세요.',
            }}
            error={!!errors.guardianInfo?.gender}
          />
          <div className="min-h-2">
            {errors.guardianInfo?.gender && (
              <ErrorInput text={errors.guardianInfo.gender.message} />
            )}
          </div>
        </div>

        <div>
          <TextWinds type={'content_body2'}>보호자 전화번호</TextWinds>
          {/* 국가 코드와 번호를 한 줄로 묶어서 입력받는다. */}
          <div className="flex gap-2">
            <div className="w-[92px] shrink-0">
              <FormRow
                control={control}
                type="country-code"
                name="guardianInfo.countryCode"
                placeholder={DEFAULT_COUNTRY_CODE}
                rules={countryCodeRules({
                  required: COUNTRY_CODE_REQUIRED_MESSAGE,
                  // 국가 코드를 바꾸면 전화번호도 새 기준으로 다시 검사한다.
                  deps: ['guardianInfo.phone'],
                })}
                error={!!errors.guardianInfo?.countryCode}
              />
            </div>
            <div className="min-w-0 flex-1">
              <FormRow
                control={control}
                type="phone"
                name="guardianInfo.phone"
                placeholder="- 없이 번호만 입력해주세요"
                rules={phoneRules({
                  label: '보호자 전화번호',
                  required: true,
                  countryCode,
                })}
                error={!!errors.guardianInfo?.phone}
                countryCode={countryCode}
              />
            </div>
          </div>
          {/* 국가 코드와 번호를 묶어서 보여주므로 오류도 한 자리에 낸다. */}
          <div className="min-h-2">
            {errors.guardianInfo?.countryCode ? (
              <ErrorInput text={errors.guardianInfo.countryCode.message} />
            ) : errors.guardianInfo?.phone ? (
              <ErrorInput text={errors.guardianInfo.phone.message} />
            ) : (
              <TextWinds type="caption1" color="gray6">
                {COUNTRY_CODE_HELP}
              </TextWinds>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 py-5">
          <TermAgreement
            agreementList={agreement}
            essentialChecked={setAgree}
          />
        </div>
      </div>
    </section>
  );
};

export default GuardianInput;
